<script lang="ts" setup>
// ================================
// IMPORTS
// ================================
// Vue
import { computed, ref } from "vue"
import { useSourceStore } from "@core/stores/source.store"
import { useI18n } from "vue-i18n"
import { formatDate } from "@core/helpers"

// Types
import {
    InformationCpcu,
    InformationDalkia,
    InformationSigdu,
    InformationPCE,
    InformationPDL,
    InformationRegaz,
    InformationElecSavoie,
    LineBlock,
    NotificationEvent,
} from "@types"
import { useTaskStore } from "@core/stores/task.store"
import { SOURCES_TYPES } from "@core/constants"
import { useEventBus, useDebounceFn } from "@vueuse/core"
import { SourceService } from "@core/services"
import {
    buildPdlReceivedDataBlocks,
    type ReceivedDataTab,
    type SensPdl,
} from "./meter-received-data-pdl.helper"
import { buildElecStrasbourgReceivedData } from "./meter-elecstrasbourg.helper"
import {
    MESSAGES_UBORDEAUX,
    buildUBordeauxReceivedData,
} from "./meter-ubordeaux.helper"

const sourceStore = useSourceStore()
const taskStore = useTaskStore()
const sourceService = new SourceService()

// ================================
// INTERNATIONALIZATION
// ================================

const { t, tm } = useI18n({
    messages: {
        fr: {
            ubordeaux: MESSAGES_UBORDEAUX,
            error: {
                inexistant:
                    "Enedis nous remonte qu’il n’y a pas de compteur existant avec ce numéro de PDL sur leur réseau de distribution",
                active_but_no_data_pce: {
                    message:
                        "Le compteur est actif mais aucune donnée n'a été reçue pour le moment.",
                    button: "Lancer une récupération des données",
                },
                active_but_no_consent: {
                    message:
                        "Le compteur est actif mais le consentement avec Homeys pour la récupération des données a été révoqué.",
                },
            },
            time_step: "Pas de temps",
            date_first_data: "Date de la première donnée",
            date_last_data: "Date de la dernière donnée",
            data_complete: "Réception à jour",
            error_data: "Erreur de réception",
            // DEV-1496 — cet onglet affiche du STOCK (« qu'est-ce qu'on détient,
            // de quand à quand »). « Réception à jour » y affirmait une FRAÎCHEUR
            // que la présence d'une `date_from` ne mesure pas. Le badge dit
            // désormais le fait qu'il lit ; la fraîcheur reste sur la bannière,
            // qui porte le verdict et l'attendu du compteur.
            data_received: "Données reçues",
            // DEV-1506 — dapi ne sert plus, pour une source Enedis, que les bornes
            // dont l'écrivain est vivant : la PMax et la réactive n'ont plus de
            // plage de dates. C'est une absence de BORNE, pas de donnée — la
            // badger en erreur affirmait un défaut là où rien n'a échoué (même
            // patron que « Pas de données publiées » côté gaz).
            no_bounds_published: "Dates non publiées",
            no_bounds_published_tooltip:
                "Ces mesures sont collectées et consultables dans les graphiques, mais nous ne disposons pas d'une plage de dates fiable à afficher ici.",
            pce_date_mise_en_service_info:
                "GRDF indique une mise en service du PCE au {date} pour le titulaire actif. GRDF ne transmet plus de données antérieures à cette date (les données déjà remontées avant restent disponibles).",
            pce_data_not_published: "Pas de données publiées",
            pce_data_not_published_tooltip:
                "GRDF ne transmet les données publiées (M023, définitives, qui servent à la facturation) que si la publication est souscrite pour ce PCE. À défaut, seul l'informatif quotidien remonte — ce n'est pas une erreur.",
            pce_waiting_first_reading: "En attente de la 1ʳᵉ relève",
            pce_waiting_first_reading_tooltip:
                "Le compteur vient d'être mis en service. Les premières données remonteront à la prochaine relève GRDF — c'est normal de ne pas encore en voir ici.",
            // ⚠️ `cdc_activating` / `cdc_activating_tooltip` sont retirés avec
            // leur branche : ils ne s'affichaient que sur
            // `data_retrieval.historical.state === "delayed"`, valeur qu'AUCUN
            // écrivain n'émet depuis DEV-1324 (dapi `_etat_historical()` rend
            // `noconsent | finished | crashed | waiting`). Cf.
            // `meter-received-data-pdl.helper.ts`.
            error_injection:
                "ENEDIS nous informe qu’il n’y pas d'injection sur ce compteur.",
            error_soutirage:
                "ENEDIS nous informe qu’il n’y pas de soutirage sur ce compteur.",
            teleoperable_banner:
                "Ce compteur n’est pas téléopérable : Enedis ne peut pas nous transmettre de courbe de charge ni de données journalières de consommation. Seules les données techniques, contractuelles et les données agrégées de facturation sont disponibles.",
            error_retry_pce_data:
                "Erreur lors du lancement de la récupération des données.",
            billing_pdl: {
                title: [
                    "Consommation",
                    "Énergie active",
                    "Période de facturation",
                ],
            },
            pdl: {
                tooltips: {
                    injection:
                        "Énergie que le compteur produit et injecte dans le réseau.",
                    soutirage:
                        "Énergie prise au réseau pour la consommation des appareils.",
                },
                buttons: {
                    soutirage: ({
                        named,
                    }: {
                        named: (key: string) => string
                    }) =>
                        `Données en soutirage - ${
                            injectionSoutirageMap[named("segment")] ||
                            named("segment")
                        }`,
                    injection: ({
                        named,
                    }: {
                        named: (key: string) => string
                    }) =>
                        `Données en injection - ${
                            soutirageInjectionMap[named("segment")] ||
                            named("segment")
                        }`,
                },
                C5: [
                    {
                        title: [
                            "Consommation",
                            "Énergie active",
                            "Courbe de charge",
                        ],
                        data: {
                            name: "Courbe de charge",
                            variable: "energie_elec_tout",
                            freq: "hourly",
                            freqName: "30 minutes",
                        },
                    },
                    {
                        title: [
                            "Consommation",
                            "Énergie active",
                            "Journalière",
                        ],
                        data: {
                            name: "Journalière",
                            variable: "energie_elec_tout",
                            freq: "daily",
                            freqName: "Journalier",
                        },
                    },
                    {
                        title: ["Puissance maximale atteinte"],
                        data: {
                            name: "Journalière",
                            variable: "puissance_elec_tout",
                            freq: "daily",
                            freqName: "Journalier",
                        },
                    },
                ],
                P4: [
                    {
                        title: ["Énergie active", "Courbe de charge"],
                        data: {
                            name: "Courbe de charge",
                            variable: "energie_elecinjection_tout",
                            freq: "hourly",
                            freqName: "30 minutes",
                        },
                    },
                    {
                        title: ["Énergie active", "Journalière"],
                        data: {
                            name: "Journalière",
                            variable: "energie_elecinjection_tout",
                            freq: "daily",
                            freqName: "Journalier",
                        },
                    },
                ],
                soutirage_above_C4: [
                    {
                        title: [
                            "Consommation",
                            "Énergie active",
                            "Courbe de charge",
                        ],
                        data: {
                            name: "Courbe de charge",
                            variable: "energie_elec_tout",
                            freq: "hourly",
                            freqName: "10 minutes",
                        },
                    },
                    {
                        title: [
                            "Consommation",
                            "Énergie active",
                            "Journalière",
                        ],
                        data: {
                            name: "Journalière",
                            variable: "energie_elec_tout",
                            freq: "daily",
                            freqName: "Journalier",
                        },
                    },
                    {
                        title: ["Puissance réactive"],
                        data: {
                            name: "Courbe de charge",
                            variable: "energie_elec_reactive",
                            freq: "hourly",
                            freqName: "10 minutes",
                        },
                    },
                ],
                injection_above_P3: [
                    {
                        title: ["Énergie active", "Courbe de charge"],
                        data: {
                            name: "Courbe de charge",
                            variable: "energie_elecinjection_tout",
                            freq: "hourly",
                            freqName: "10 minutes",
                        },
                    },
                    {
                        title: ["Énergie active", "Journalière"],
                        data: {
                            name: "Journalière",
                            variable: "energie_elecinjection_tout",
                            freq: "daily",
                            freqName: "Journalier",
                        },
                    },
                    {
                        title: ["Énergie réactive"],
                        data: {
                            name: "Courbe de charge",
                            variable: "energie_elecinjection_reactive",
                            freq: "hourly",
                            freqName: "10 minutes",
                        },
                    },
                ],
            },
            pce: {
                published_data: "Données publiées",
                informative_data: "Données informatives",
                definitive: "Définitives",
                provisional: "Provisoires",
                "6M": [
                    {
                        title: ["Données publiées"],
                        description:
                            "Données individuelles de consommation transmises au fournisseur de gaz et qui servent à la facturation.",
                        data: {
                            variable: "energie_gaz_tout",
                            freq: "periode",
                            freqName: "Semestrielle",
                            description:
                                "Données de consommation publiées par période (relève tous les 6 mois), après consolidation des caractéristiques du gaz consommé.",
                        },
                    },
                ],
                "1M": [
                    {
                        title: ["Données publiées"],
                        description:
                            "Données individuelles de consommation transmises au fournisseur de gaz et qui servent à la facturation.",
                        data: {
                            variable: "energie_gaz_tout",
                            freq: "periode",
                            freqName: "Mensuelle",
                            description:
                                "Données de consommation publiées par période mensuelle, après consolidation des caractéristiques du gaz consommé.",
                        },
                    },
                    {
                        title: ["Données informatives"],
                        description:
                            "Données individuelles de consommation à une maille plus fine que les données publiées.",
                        data: {
                            variable: "energie_gaz_tout",
                            freq: "daily",
                            freqName: "Quotidienne",
                            description:
                                "Données quotidiennes estimées, transmises plus rapidement que les données publiées (peuvent être révisées).",
                        },
                    },
                ],
                MM: [
                    {
                        title: ["Données publiées"],
                        description:
                            "Données individuelles de consommation transmises au fournisseur de gaz et qui servent à la facturation.",
                        data: {
                            variable: "energie_gaz_tout",
                            freq: "periode",
                            freqName: "Mensuelle",
                            description:
                                "Données de consommation publiées par période mensuelle, après consolidation des caractéristiques du gaz consommé.",
                        },
                    },
                    {
                        title: ["Données informatives"],
                        description:
                            "Données individuelles de consommation à une maille plus fine que les données publiées.",
                        data: {
                            variable: "energie_gaz_tout",
                            freq: "daily",
                            freqName: "Quotidienne",
                            description:
                                "Données quotidiennes estimées, transmises plus rapidement que les données publiées (peuvent être révisées).",
                        },
                    },
                ],
                JJ: [
                    {
                        title: ["Données publiées"],
                        description:
                            "Données individuelles de consommation transmises au fournisseur de gaz et qui servent à la facturation.",
                        data: {
                            variable: "energie_gaz_tout",
                            freq: "periode",
                            freqName: "Quotidienne",
                            description:
                                "Données de consommation publiées (télérelevé quotidien), après consolidation des caractéristiques du gaz consommé.",
                        },
                    },
                    {
                        title: ["Données informatives"],
                        description:
                            "Données individuelles de consommation à une maille plus fine que les données publiées.",
                        data: {
                            variable: "energie_gaz_tout",
                            freq: "daily",
                            freqName: "Quotidienne",
                            description:
                                "Données quotidiennes estimées, transmises plus rapidement que les données publiées (peuvent être révisées).",
                        },
                    },
                ],
            },
            cpcu: [
                {
                    title: ["Consommation", "Énergie de chaleur"],
                    data: {
                        variable: "energie_chaleur_tout",
                        freq: "daily",
                        freqName: "Journalier",
                    },
                },
                {
                    title: ["Consommation", "Volume de chaleur"],
                    data: {
                        variable: "volume_chaleur_tout",
                        freq: "daily",
                        freqName: "Journalier",
                    },
                },
            ],
            dalkia: [
                {
                    title: ["Consommation", "Énergie de chaleur"],
                    data: {
                        variable: "energie_chaleur_tout",
                        freq: "daily",
                        freqName: "Journalier",
                    },
                },
            ],
            sigdu: [
                {
                    title: ["Consommation d'eau"],
                    data: {
                        variable: "volume_eau_tout",
                        freq: "daily",
                        freqName: "Journalier",
                    },
                },
            ],
            regaz: [
                {
                    title: ["Consommation", "Énergie de gaz"],
                    data: {
                        variable: "energie_gaz_tout",
                        freq: "daily",
                        freqName: "Journalier",
                    },
                },
                {
                    title: ["Consommation", "Volume de gaz"],
                    data: {
                        variable: "volume_gaz_tout",
                        freq: "daily",
                        freqName: "Journalier",
                    },
                },
            ],
            elecstrasbourg: {
                flux: {
                    categorie: "Consommation",
                    pas: "Pas de temps",
                    premiere_donnee: "Date de la première donnée",
                    derniere_donnee: "Date de la dernière donnée",
                    recu: "Données reçues",
                    non_recu: "Aucune donnée reçue",
                    energie_journaliere: {
                        titre: "Énergie active journalière",
                        pas: "Journalier",
                        non_recu_tooltip:
                            "Aucune énergie journalière n'a encore été transmise par Strasbourg Électricité Réseaux pour ce compteur.",
                    },
                    energie_horaire: {
                        titre: "Énergie active horaire",
                        pas: "Horaire",
                        non_recu_tooltip:
                            "L'énergie horaire se calcule depuis la courbe de charge : elle n'existe que si la courbe de charge est transmise pour ce compteur.",
                    },
                    courbe_de_charge: {
                        titre: "Courbe de charge",
                        pas: "30 minutes",
                        non_recu_tooltip:
                            "La courbe de charge n'est transmise que si le titulaire du contrat l'a activée auprès de Strasbourg Électricité Réseaux. Les autres données restent disponibles.",
                    },
                    puissance_max: {
                        titre: "Puissance maximale journalière",
                        pas: "Journalier",
                        non_recu_tooltip:
                            "Aucune puissance maximale n'a encore été transmise par Strasbourg Électricité Réseaux pour ce compteur.",
                    },
                },
            },
            elecsavoie: [
                {
                    title: ["Consommation", "Énergie de d'électricité"],
                    data: {
                        variable: "energie_elec_tout",
                        freq: "daily",
                        freqName: "Journalier",
                    },
                },
                {
                    title: ["Consommation", "Puissance"],
                    data: {
                        variable: "puissance_elec_tout",
                        freq: "daily",
                        freqName: "Journalier",
                    },
                },
            ],
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
type Props = {
    meterId: string
}

type Badge = {
    text: string
    icon: string
    status: "success" | "warn" | "info"
    tooltip?: string
}

// ================================
// PROPS & EMITS
// ================================
const props = defineProps<Props>()

// ================================
// CONSTANTS
// ================================
const soutirageInjectionMap: Record<string, string> = {
    C5: "P4",
    C4: "P3",
    C2: "P1",
}

const injectionSoutirageMap: Record<string, string> = {
    P4: "C5",
    P3: "C4",
    P1: "C2",
    null: "",
}

const options = ref(["soutirage", "injection"])

// ================================
// COMPUTED PROPERTIES
// ================================

/**
 * Computed pour récupérer les infos de la source
 */
const getSourceById = computed(() => {
    return sourceStore.getSourceById(props.meterId)
})

/**
 * PDL dont Enedis indique l’absence de téléopérabilité (courbes / journalier indisponibles à distance).
 */
const getIsPdlNonTeleoperable = computed(() => {
    return (
        getSourceById.value?.type === SOURCES_TYPES.PDL &&
        getSourceById.value?.informations?.pdl?.compteur?.teleoperable === false
    )
})

// ================================
// REACTIVE STATE
// ================================
const valueButton = ref(
    soutirageInjectionMap[
        getSourceById.value?.informations?.pdl?.compteur?.segment ?? ""
    ]
        ? "soutirage"
        : "injection"
)

/**
 * Computed pour récupérer les erreurs Bloquant l'affichage de données ex: compteur inexistant
 */
const getMajorError = computed(() => {
    if (
        getSourceById?.value?.type === SOURCES_TYPES.PDL &&
        getSourceById?.value?.informations?.pdl?.etat === "inexistant"
    )
        return {
            message: t("error.inexistant"),
        }
    if (
        getSourceById?.value?.type === SOURCES_TYPES.PCE &&
        getSourceById?.value?.informations?.pce?.droit_access?.etat ===
            "Active" &&
        getSourceById?.value?.informations?.pce?.compteur.frequence === null
    ) {
        // Si le consentetement est révoqué, on n'affiche pas le message d'erreur pour lancer une récupération des données
        if (
            getSourceById?.value?.informations?.pce?.consentement?.etat !==
            "active"
        ) {
            return {
                message: t("error.active_but_no_consent.message"),
            }
        }

        return {
            message: t("error.active_but_no_data_pce.message"),
            button: t("error.active_but_no_data_pce.button"),
            action: "retry_pce_data",
        }
    }

    return null
})

const isFetchingTask = ref(false)

const bus = useEventBus<string>("update:source_informations")
const busNotification = useEventBus<NotificationEvent>("notification_toast")

async function handleMajorErrorActionCore() {
    if (!getMajorError.value?.action || isFetchingTask.value) return

    switch (getMajorError.value.action) {
        case "retry_pce_data": {
            try {
                // Lancer une tâche de récupération des données PCE.
                // DEV-527 — la collecte grdf passe par harvest : on déclenche la
                // collecte on-demand via api-metier (routage source→collecteur,
                // PCE grdf → harvest) et on suit le task_id renvoyé (toast +
                // subscribe), au lieu d'enfiler directement le getlastdata legacy.
                isFetchingTask.value = true
                const { task_id } = await sourceService.triggerSourceDataUpdate(
                    props.meterId
                )
                if (!task_id) {
                    throw new Error("Aucun task_id renvoyé par la collecte")
                }
                taskStore.followEvent(task_id, task_id, {
                    taskname: "workers2app.getlastdata_updateapp.request",
                    autoDelete: true,
                    onError: () => {
                        isFetchingTask.value = false
                    },
                    onSuccess: () => {
                        bus.emit(props.meterId)
                        isFetchingTask.value = false
                    },
                })
            } catch (error) {
                console.error("Error launching PCE data retrieval task:", error)
                busNotification.emit({
                    severity: "error",
                    title: t("error_retry_pce_data"),
                    life: 3000,
                })
                isFetchingTask.value = false
            }

            break
        }
        default:
            console.warn("Unknown action:", getMajorError.value.action)
    }
}

// Debounce de 1 seconde pour éviter les clics multiples
const handleMajorErrorAction = useDebounceFn(handleMajorErrorActionCore, 1000, {
    maxWait: 1000,
})

/**
 * Computed pour récupérer les infos selon le type decompteur
 */
const getReceivedDataByType = computed(() => {
    let result: LineBlock[] | null = []
    switch (getSourceById.value?.type) {
        case SOURCES_TYPES.PCE:
            result = getReceivedDataPCE()
            break
        case SOURCES_TYPES.PDL:
            result = getReceivedDataPDL()
            break
        case SOURCES_TYPES.CPCU:
            result = getReceivedDataCPCU()
            break
        case SOURCES_TYPES.DALKIA:
            result = getReceivedDataDalkia()
            break
        case SOURCES_TYPES.REGAZ:
            result = getReceivedDataRegaz()
            break
        case SOURCES_TYPES.ELECSAVOIE:
            result = getReceivedDataElecSavoie()
            break
        case SOURCES_TYPES.ELECSTRASBOURG:
            result = buildElecStrasbourgReceivedData(
                getSourceById.value?.informations?.elecstrasbourg,
                t
            )
            break
        case SOURCES_TYPES.SIGDU:
            result = getReceivedDataSigdu()
            break
        case SOURCES_TYPES.UBORDEAUX:
            result = buildUBordeauxReceivedData(
                getSourceById.value?.informations?.ubordeaux,
                t
            )
            break
    }

    return result
})

/**
 * Message informatif PCE : date de mise en service fournie par GRDF.
 * Sert à expliquer pourquoi aucune donnée n'est remontée avant cette date.
 */
const getPceDateMiseEnServiceMessage = computed(() => {
    if (getSourceById.value?.type !== SOURCES_TYPES.PCE) return null
    const date = getSourceById.value?.informations?.pce?.date_mise_en_service
    if (!date) return null
    return t("pce_date_mise_en_service_info", { date: formatDate(date) })
})

/**
 * Computed pour récupérer les infos selon le type decompteur
 */
const getErrors = computed(() => {
    if (getIsPdlNonTeleoperable.value) return null
    if (
        valueButton.value === "soutirage" &&
        getSourceById.value?.type === SOURCES_TYPES.PDL &&
        !getSourceById.value?.informations?.pdl?.data?.soutirage?.has_soutirage
    )
        return t("error_soutirage")
    if (
        valueButton.value === "injection" &&
        getSourceById.value?.type === SOURCES_TYPES.PDL &&
        !getSourceById.value?.informations?.pdl?.data?.injection?.has_injection
    )
        return t("error_injection")
    return null
})

// ================================
// METHODS
// ================================

type ReceivedDataTimeSlot = {
    date_from?: string | Date | null
    date_to?: string | Date | null
}

/**
 * Extrait le créneau (raw, daily, hourly…) d’une entrée `_variables` pour l’affichage des dates.
 * @param variable - Ligne variable telle que renvoyée par l’API
 * @param freq - Clé de fréquence (ex. daily, hourly, raw)
 * @returns Objet avec date_from / date_to ou null
 */
function getDataSlotFromVariable(
    variable: unknown,
    freq: string
): ReceivedDataTimeSlot | null {
    if (!variable || typeof variable !== "object" || !freq) return null
    const slot = (variable as Record<string, unknown>)[freq]
    if (
        slot &&
        typeof slot === "object" &&
        slot !== null &&
        "date_from" in slot
    ) {
        return slot as ReceivedDataTimeSlot
    }
    return null
}

/**
 * Délai (en jours) avant la 1ʳᵉ relève GRDF selon la fréquence de relève du PCE.
 * Tant que la mise en service est plus récente que ce délai, l'absence de donnée
 * publiée est normale (la relève n'a pas encore eu lieu).
 */
function pceFirstReadingDelayDays(frequence?: string | null): number {
    switch (frequence) {
        case "6M":
            return 186 // ~6 mois
        case "JJ":
            return 5 // relève quotidienne → données sous quelques jours
        case "1M":
        case "MM":
        default:
            return 32 // ~1 mois
    }
}

/**
 * Récupère les infos sur les données d'un compteur PCE
 */
const getReceivedDataPCE = () => {
    const pce = getSourceById.value?.informations?.pce as InformationPCE
    const freq = pce?.compteur?.frequence
    const tabs: {
        title: string[]
        description?: string
        data: { name: string; variable: string; freq: string; freqName: string }
    }[] = tm(`pce.${freq}`) || []
    if (!Array.isArray(tabs) || tabs.length === 0) return null
    const formattedTabs: LineBlock[] = []

    tabs?.forEach((element, index) => {
        const variable = pce.data._variables?.find(
            (v) => v.type === element?.data?.variable
        )
        const grain = element?.data?.freq ?? ""
        const data = getDataSlotFromVariable(variable, grain)

        // Délai de relève seulement pertinent pour le publié (periode) ; l'informatif
        // (daily) remonte sous quelques jours après la mise en service.
        const mesDate = pce?.date_mise_en_service
            ? new Date(pce.date_mise_en_service)
            : null
        const waitDays =
            grain === "periode" ? pceFirstReadingDelayDays(freq) : 5
        const isWaitingFirstReading =
            !!mesDate &&
            Date.now() - mesDate.getTime() < waitDays * 24 * 60 * 60 * 1000

        let badge: Badge
        if (data?.date_from) {
            badge = {
                text: t("data_complete"),
                icon: "pi pi-check-circle",
                status: "success",
            }
        } else if (isWaitingFirstReading) {
            badge = {
                text: t("pce_waiting_first_reading"),
                icon: "pi pi-clock",
                status: "info",
                tooltip: t("pce_waiting_first_reading_tooltip"),
            }
        } else if (grain === "periode") {
            // Bloc « Données publiées » sans donnée : la souscription à la publication
            // M023 n'est pas exposée par dapi, donc on n'affiche pas une erreur
            // alarmante — l'absence de publié est le cas normal d'un PCE informatif-only.
            badge = {
                text: t("pce_data_not_published"),
                icon: "pi pi-info-circle",
                status: "info",
                tooltip: t("pce_data_not_published_tooltip"),
            }
        } else {
            // Bloc « Données informatives » (quotidien) réellement absent = vrai défaut.
            badge = {
                text: t("error_data"),
                icon: "pi pi-times",
                status: "warn",
            }
        }

        formattedTabs[index] = {
            title: element?.title,
            badge: [badge],
            lines: [
                {
                    text: t("time_step"),
                    value: element?.data?.freqName,
                },
                {
                    text: t("date_first_data"),
                    value: data?.date_from ? formatDate(data?.date_from) : "-",
                },
                {
                    text: t("date_last_data"),
                    value: data?.date_to ? formatDate(data?.date_to) : "-",
                },
            ],
        }
    })
    return formattedTabs
}

/**
 * Récupère les infos sur les données d'un compteur PDL
 */
const getReceivedDataPDL = () => {
    const pdlNonTeleop = getIsPdlNonTeleoperable.value
    if (
        !pdlNonTeleop &&
        valueButton.value === "soutirage" &&
        !getSourceById.value?.informations?.pdl?.data?.soutirage?.has_soutirage
    )
        return null
    if (
        !pdlNonTeleop &&
        valueButton.value === "injection" &&
        !getSourceById.value?.informations?.pdl?.data?.injection?.has_injection
    )
        return null

    // Informations du PDL
    const pdl = getSourceById.value?.informations?.pdl as InformationPDL
    // Segment du compteur
    const segment = pdl?.compteur?.segment ?? ""

    // Correction du segment en fonction du type de données (soutirage/injection)
    let correctSegment = ""
    if (valueButton.value === "soutirage")
        correctSegment = injectionSoutirageMap?.[segment] || segment
    else correctSegment = soutirageInjectionMap[segment] || segment
    if (correctSegment !== "C5" && correctSegment.startsWith("C")) {
        correctSegment = "soutirage_above_C4"
    } else if (correctSegment !== "P4" && correctSegment.startsWith("P")) {
        correctSegment = "injection_above_P3"
    }

    const tabs: ReceivedDataTab[] = tm(`pdl.${correctSegment}`) || []

    // DEV-1496 : la construction des blocs vit dans un helper PUR — la courbe de
    // charge y lit son bloc dédié (dates ET pas), les lignes sans borne servie
    // ne sont plus badgées en erreur, et la réactive n'hérite plus du badge de
    // la CDC active.
    return buildPdlReceivedDataBlocks(
        pdl,
        tabs,
        valueButton.value as SensPdl,
        t,
        (d) => formatDate(d),
        tm("billing_pdl.title") as string[]
    )
}

/**
 * Récupère les infos sur les données d'un compteur CPCU
 */
const getReceivedDataCPCU = () => {
    // Informations du CPCU
    const cpcu = getSourceById.value?.informations?.cpcu as InformationCpcu
    const tabs = tm("cpcu") || []
    const formattedTabs: LineBlock[] = []

    tabs?.forEach((element, index) => {
        const variable = cpcu.data._variables?.find(
            (v) => v.type === element?.data?.variable
        )
        const data = getDataSlotFromVariable(
            variable,
            element?.data?.freq ?? ""
        )
        formattedTabs[index] = {
            title: element?.title,
            badge: [
                {
                    text: data?.date_from
                        ? t("data_complete")
                        : t("error_data"),
                    icon: data?.date_from ? "pi pi-wifi" : "pi pi-times",
                    status: data?.date_from ? "success" : "warn",
                },
            ],
            lines: [
                {
                    text: t("time_step"),
                    value: element?.data?.freqName,
                },
                {
                    text: t("date_first_data"),
                    value: data?.date_from ? formatDate(data?.date_from) : "-",
                },
                {
                    text: t("date_last_data"),
                    value: data?.date_to ? formatDate(data?.date_to) : "-",
                },
            ],
        }
    })
    return formattedTabs
}
const getReceivedDataDalkia = () => {
    // Informations du Dalkia
    const dalkia = getSourceById.value?.informations
        ?.dalkia as InformationDalkia
    const tabs = tm("dalkia") || []
    const formattedTabs: LineBlock[] = []

    tabs?.forEach((element, index) => {
        const variable = dalkia.data._variables?.find(
            (v) => v.type === element?.data?.variable
        )
        const data = getDataSlotFromVariable(
            variable,
            element?.data?.freq ?? ""
        )
        formattedTabs[index] = {
            title: element?.title,
            badge: [
                {
                    text: data?.date_from
                        ? t("data_complete")
                        : t("error_data"),
                    icon: data?.date_from ? "pi pi-wifi" : "pi pi-times",
                    status: data?.date_from ? "success" : "warn",
                },
            ],
            lines: [
                {
                    text: t("time_step"),
                    value: element?.data?.freqName,
                },
                {
                    text: t("date_first_data"),
                    value: data?.date_from ? formatDate(data?.date_from) : "-",
                },
                {
                    text: t("date_last_data"),
                    value: data?.date_to ? formatDate(data?.date_to) : "-",
                },
            ],
        }
    })
    return formattedTabs
}

/**
 * Récupère les infos sur les données d'un compteur SIGDU
 */
function getReceivedDataSigdu() {
    // Informations du compteur SIGDU
    const sigdu = getSourceById.value?.informations?.sigdu as InformationSigdu
    const tabs = tm("sigdu") || []
    const formattedTabs: LineBlock[] = []

    tabs?.forEach((element, index) => {
        const variable = sigdu.data._variables?.find(
            (v) => v.type === element?.data?.variable
        )
        const data = getDataSlotFromVariable(
            variable,
            element?.data?.freq ?? ""
        )
        formattedTabs[index] = {
            title: element?.title,
            badge: [
                {
                    text: data?.date_from
                        ? t("data_complete")
                        : t("error_data"),
                    icon: data?.date_from ? "pi pi-wifi" : "pi pi-times",
                    status: data?.date_from ? "success" : "warn",
                },
            ],
            lines: [
                {
                    text: t("time_step"),
                    value: element?.data?.freqName,
                },
                {
                    text: t("date_first_data"),
                    value: data?.date_from ? formatDate(data?.date_from) : "-",
                },
                {
                    text: t("date_last_data"),
                    value: data?.date_to ? formatDate(data?.date_to) : "-",
                },
            ],
        }
    })
    return formattedTabs
}
/**
 * Récupère les infos sur les données d'un compteur Regaz
 */
const getReceivedDataRegaz = () => {
    // Informations du Regaz
    const regaz = getSourceById.value?.informations?.regaz as InformationRegaz
    const tabs = tm("regaz") || []
    const formattedTabs: LineBlock[] = []

    tabs?.forEach((element, index) => {
        const variable = regaz.data._variables?.find(
            (v) => v.type === element?.data?.variable
        )
        const data = getDataSlotFromVariable(
            variable,
            element?.data?.freq ?? ""
        )
        formattedTabs[index] = {
            title: element?.title,
            badge: [
                {
                    text: data?.date_from
                        ? t("data_complete")
                        : t("error_data"),
                    icon: data?.date_from ? "pi pi-wifi" : "pi pi-times",
                    status: data?.date_from ? "success" : "warn",
                },
            ],
            lines: [
                {
                    text: t("time_step"),
                    value: element?.data?.freqName,
                },
                {
                    text: t("date_first_data"),
                    value: data?.date_from ? formatDate(data?.date_from) : "-",
                },
                {
                    text: t("date_last_data"),
                    value: data?.date_to ? formatDate(data?.date_to) : "-",
                },
            ],
        }
    })
    return formattedTabs
}
/**
 * Récupère les infos sur les données d'un compteur Regaz
 */
const getReceivedDataElecSavoie = () => {
    // Informations du ElecSavoie
    const elecsavoie = getSourceById.value?.informations
        ?.elecsavoie as InformationElecSavoie
    const tabs = tm("elecsavoie") || []
    const formattedTabs: LineBlock[] = []

    tabs?.forEach((element, index) => {
        const variable = elecsavoie.data._variables?.find(
            (v) => v.type === element?.data?.variable
        )
        const data = getDataSlotFromVariable(
            variable,
            element?.data?.freq ?? ""
        )
        formattedTabs[index] = {
            title: element?.title,
            badge: [
                {
                    text: data?.date_from
                        ? t("data_complete")
                        : t("error_data"),
                    icon: data?.date_from ? "pi pi-wifi" : "pi pi-times",
                    status: data?.date_from ? "success" : "warn",
                },
            ],
            lines: [
                {
                    text: t("time_step"),
                    value: element?.data?.freqName,
                },
                {
                    text: t("date_first_data"),
                    value: data?.date_from ? formatDate(data?.date_from) : "-",
                },
                {
                    text: t("date_last_data"),
                    value: data?.date_to ? formatDate(data?.date_to) : "-",
                },
            ],
        }
    })
    return formattedTabs
}
</script>

<template>
    <div class="meter_card_received_data mt-6 flex flex-col gap-6">
        <!-- si c'est un PDL on peut sélectionner soutirage ou injection  -->
        <BaseMessage
            v-if="getIsPdlNonTeleoperable && !getMajorError"
            severity="error"
            class="w-full"
        >
            {{ t("teleoperable_banner") }}
        </BaseMessage>
        <BaseSelectButton
            v-if="getSourceById?.type === 'pdl' && !getMajorError"
            v-model="valueButton"
            :options="options"
            :allow-empty="false"
        >
            <template #option="slotProps">
                {{
                    t(`pdl.buttons.${slotProps.option}`, {
                        segment:
                            getSourceById?.informations?.pdl?.compteur?.segment,
                    })
                }}
                <BaseIcon
                    icon="pi pi-exclamation-circle"
                    v-tooltip.top="t(`pdl.tooltips.${slotProps.option}`)"
                />
            </template>
        </BaseSelectButton>
        <!-- messages d'erreur TODO dans certains cas l'utilisateurs doit pouvoir déclarer du souritrage -->
        <div v-if="getErrors && !getMajorError">
            <BaseMessage severity="warn">{{ getErrors }}</BaseMessage>
        </div>
        <div
            v-if="getPceDateMiseEnServiceMessage && !getMajorError"
            class="pce_date_mise_en_service_hint"
        >
            <BaseIcon icon="pi pi-info-circle" />
            <span>{{ getPceDateMiseEnServiceMessage }}</span>
        </div>
        <!-- afficher les infos selon le type -->
        <div v-if="getReceivedDataByType" class="flex flex-col gap-6">
            <div
                v-for="(lineBlocks, index) in getReceivedDataByType"
                :key="index"
            >
                <MeterCardLinesBlocks :line-blocks="lineBlocks" />
            </div>
        </div>
        <!-- messages d'errreurs empéchant l'affichage de données ex: compteur inexistant-->
        <div v-if="getMajorError">
            <BaseMessage severity="warn">
                <p>{{ getMajorError.message }}</p>
                <div v-if="getMajorError.button" class="mt-6">
                    <BaseButton
                        severity="primary"
                        @click="handleMajorErrorAction"
                        :label="getMajorError.button"
                        :loading="isFetchingTask"
                    />
                </div>
            </BaseMessage>
        </div>
    </div>
</template>

<style scoped>
:deep(.p-togglebutton-checked) {
    background: var(--p-blue-900) !important;
    color: var(--color-white) !important;
}

.pce_date_mise_en_service_hint {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    font-size: 0.8125rem;
    color: var(--p-surface-500);
    line-height: 1.25rem;
}
</style>
